import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import api from "../../lib/api";
import { reportError } from "../../lib/errorReporter";
import { apiFieldErrors } from "../../lib/companyValidation";
import {
  MAX_INTERNAL_NOTE,
  MAX_ORGANIZATION_NAME,
  platformErrorMessage,
  validateOrganizationName,
  validateOwnerContact,
  validateSeatLimit,
} from "../../lib/platformValidation";
import CompanyField from "../company/CompanyField";
import PlatformOwnerFields from "./PlatformOwnerFields";

const EMPTY = {
  name: "",
  owner_first_name: "",
  owner_last_name: "",
  owner_email: "",
  owner_phone: "",
  manager_seat_limit: "0",
  sales_seat_limit: "1",
  internal_note: "",
};

/**
 * Create a customer organization from the platform console.
 *
 * The organization is created in ``pending_activation`` with NO owner membership
 * and exactly one pending Owner invitation. The e-mail is sent AFTER that
 * transaction commits, so a delivery failure still leaves a visible, resendable
 * invitation — which is why this dialog reports the delivery outcome separately
 * from the creation outcome.
 *
 * Not part of the accepted surface, on purpose: the owner seat limit (always 1),
 * the lifecycle state, the license status, the invitation token and the request
 * key. The backend generates or pins every one of them.
 */
export const PlatformOrgCreateDialog = ({ open, onCancel, onCreated }) => {
  const { t } = useTranslation();
  const [draft, setDraft] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [formError, setFormError] = useState("");
  const [busy, setBusy] = useState(false);
  const inFlight = useRef(false);

  if (!open) return null;

  const setField = (field, value) => {
    setDraft((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => (prev[field] ? { ...prev, [field]: undefined } : prev));
    setFormError("");
  };

  const validate = () => {
    const found = validateOwnerContact(draft);
    const name = validateOrganizationName(draft.name);
    if (name) found.name = name;
    const manager = validateSeatLimit("manager", draft.manager_seat_limit, 0);
    if (manager) found.manager_seat_limit = manager;
    const sales = validateSeatLimit("sales", draft.sales_seat_limit, 0);
    if (sales) found.sales_seat_limit = sales;
    if (draft.internal_note.trim().length > MAX_INTERNAL_NOTE) {
      found.internal_note = t("platform.internal_note_too_long");
    }
    return found;
  };

  const submit = async (event) => {
    event.preventDefault();
    if (inFlight.current || busy) return;
    const found = validate();
    if (Object.keys(found).length) {
      setErrors(found);
      return;
    }
    setErrors({});
    inFlight.current = true;
    setBusy(true);
    try {
      const r = await api.post(
        "/platform-admin/organizations",
        {
          name: draft.name.trim(),
          owner_first_name: draft.owner_first_name.trim(),
          owner_last_name: draft.owner_last_name.trim(),
          owner_email: draft.owner_email.trim(),
          owner_phone: draft.owner_phone.trim() || null,
          manager_seat_limit: Number(draft.manager_seat_limit),
          sales_seat_limit: Number(draft.sales_seat_limit),
          internal_note: draft.internal_note.trim() || null,
        },
        { skipOrgHeader: true },
      );
      setDraft(EMPTY);
      onCreated(r.data);
    } catch (err) {
      reportError("platform.createOrganization", err);
      const fields = apiFieldErrors(err);
      if (fields) setErrors(fields);
      else setFormError(platformErrorMessage(err, t("common.error_generic")));
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  };

  return (
    <div className="qw-modal-backdrop">
      <div
        className="qw-modal qw-modal-wide"
        role="dialog"
        aria-modal="true"
        aria-labelledby="platform-create-title"
        data-testid="platform-create-dialog"
      >
        <div className="h" id="platform-create-title" data-testid="platform-create-dialog-title">
          {t("platform.create_title")}
        </div>
        <form onSubmit={submit} noValidate>
          <div className="body">
            <p className="qw-help" style={{ marginTop: 0 }} data-testid="platform-create-lead">
              {t("platform.create_lead")}
            </p>

            <CompanyField
              id="platform-create-name"
              testId="platform-create-name"
              label={t("platform.org_name")}
              value={draft.name}
              onChange={(v) => setField("name", v)}
              error={errors.name}
              help={t("platform.org_name_help")}
              disabled={busy}
              maxLength={MAX_ORGANIZATION_NAME}
              required
            />

            <div style={{ fontWeight: 600, margin: "14px 0 8px" }}>
              {t("platform.owner_section")}
            </div>
            <PlatformOwnerFields
              idPrefix="platform-create-owner"
              draft={draft}
              errors={errors}
              disabled={busy}
              onChange={setField}
            />

            <div style={{ fontWeight: 600, margin: "14px 0 8px" }}>
              {t("platform.seats_section")}
            </div>
            <div className="qw-grid cols-2">
              <CompanyField
                id="platform-create-manager-seats"
                testId="platform-create-manager-seats"
                label={t("platform.manager_seats")}
                type="number"
                value={draft.manager_seat_limit}
                onChange={(v) => setField("manager_seat_limit", v)}
                error={errors.manager_seat_limit}
                help={t("platform.manager_seats_help")}
                disabled={busy}
                inputMode="numeric"
              />
              <CompanyField
                id="platform-create-sales-seats"
                testId="platform-create-sales-seats"
                label={t("platform.sales_seats")}
                type="number"
                value={draft.sales_seat_limit}
                onChange={(v) => setField("sales_seat_limit", v)}
                error={errors.sales_seat_limit}
                help={t("platform.sales_seats_help")}
                disabled={busy}
                inputMode="numeric"
              />
            </div>

            <CompanyField
              id="platform-create-note"
              testId="platform-create-note"
              label={t("platform.platform_note")}
              value={draft.internal_note}
              onChange={(v) => setField("internal_note", v)}
              error={errors.internal_note}
              help={t("platform.platform_note_help")}
              disabled={busy}
              multiline
              rows={3}
              maxLength={MAX_INTERNAL_NOTE}
            />

            {formError && (
              <div className="qw-error" data-testid="platform-create-error">
                {formError}
              </div>
            )}
          </div>
          <div className="footer">
            <button
              type="button"
              className="qw-btn ghost"
              onClick={onCancel}
              disabled={busy}
              data-testid="platform-create-cancel"
            >
              {t("platform.cancel")}
            </button>
            <button
              type="submit"
              className="qw-btn primary"
              disabled={busy}
              data-testid="platform-create-submit"
            >
              {busy ? t("platform.creating") : t("platform.create_submit")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PlatformOrgCreateDialog;
